import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";
import { ScoreRing, gradeOf } from "@/components/ScoreBadge";
import { useLang } from "@/lib/i18n";

export default function ReportsPage() {
  const { t } = useLang();
  const [reports, setReports] = useState([]);
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([api.get("/reports/weekly"), api.get("/reports/email-status")]).then(([r, e]) => { setReports(r.data); setEmail(e.data); });
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try { await api.post("/reports/weekly/generate"); toast.success(t("reports.generated")); load(); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const latest = reports[0];
  const trend = reports.slice(0, 12).reverse();

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow mb-1">{t("reports.eyebrow")}</p><h1 className="text-4xl tracking-tight">{t("reports.title")}</h1></div>
        <Button onClick={generate} disabled={busy} className="bg-primary text-white hover:bg-[hsl(var(--primary-light))] h-11" data-testid="reports-generate-btn"><RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> {t("reports.generate")}</Button>
      </div>

      {email && !email.configured && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-300 bg-amber-50 text-sm" data-testid="reports-email-pending">
          <Mail className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <p className="text-amber-900">{t("reports.email.pending")}</p>
        </div>
      )}

      {email && email.configured && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-300 bg-emerald-50 text-sm" data-testid="reports-email-ready">
          <Mail className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
          <p className="text-emerald-900">{t("reports.email.ready")}</p>
        </div>
      )}

      {latest && (
        <div className="grid md:grid-cols-[auto_1fr] gap-4">
          <div className="hairline p-6 flex items-center gap-5" data-testid="reports-latest-score">
            <ScoreRing score={latest.score} size={120} stroke={10} />
            <div>
              <p className="eyebrow mb-1">{t("score.title")} · {latest.week}</p>
              <Trend cur={latest.score} prev={latest.prev_score} />
            </div>
          </div>
          <div className="hairline p-6">
            <p className="eyebrow mb-4">{t("reports.trend")}</p>
            <div className="flex items-end gap-2 h-24" data-testid="reports-trend">
              {trend.map((r) => (
                <div key={r.id} className="flex-1 max-w-[56px] flex flex-col items-center gap-1" title={`${r.week} · ${r.score}`}>
                  <div className="w-full rounded-t-md bg-primary/80 transition-[height] duration-500" style={{ height: `${Math.max(6, r.score * 0.8)}px`, background: gradeOf(r.score) === "A" ? "#4CAF6A" : gradeOf(r.score) <= "B" ? "#7CB342" : gradeOf(r.score) === "C" ? "#F59E0B" : "#EF4444" }} />
                  <span className="font-mono text-[9px] text-muted-foreground">{r.score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <section className="hairline">
        {reports.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground">{t("reports.empty")}</p>}
        {reports.map((r, i) => (
          <details key={r.id} className={`group ${i > 0 ? "border-t border-border" : ""}`} data-testid={`report-${r.id}`}>
            <summary className="p-5 grid md:grid-cols-[90px_1fr_auto_auto] gap-4 items-center cursor-pointer list-none hover:bg-muted/40">
              <span className="font-mono text-xs text-muted-foreground">{r.week}</span>
              <div>
                <p className="font-medium">{t("reports.item")} · {new Date(r.created_at).toLocaleDateString()} <span className="text-xs text-muted-foreground font-mono">({r.trigger})</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">{r.claims_total} {t("dash.metrics.claims").toLowerCase()} · {r.new_claims_count} {t("reports.new")} · {r.alerts_count} {t("nav.alerts").toLowerCase()}</p>
              </div>
              <span className={`font-serif text-2xl ${r.score >= 75 ? "text-[hsl(142_60%_32%)]" : r.score >= 55 ? "text-amber-600" : "text-red-600"}`}>{r.score}</span>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono px-2 py-1 rounded-full uppercase ${r.email_status === "sent" ? "tag-green" : r.email_status === "failed" ? "tag-red" : "tag-orange"}`} data-testid={`report-email-${r.id}`}>{r.email_status === "sent" ? t("reports.sent") : r.email_status === "failed" ? t("reports.email.failed") : t("reports.pending")}</span>
                {email?.configured && (
                  <button type="button" onClick={async (e) => { e.preventDefault(); e.stopPropagation(); try { const { data } = await api.post(`/reports/weekly/${r.id}/resend`); toast.success(data.status === "sent" ? t("reports.email.resent") : t("reports.email.failed")); load(); } catch { toast.error(t("reports.email.failed")); } }} className="text-[10px] font-mono px-2 py-1 rounded-full border border-border hover:bg-muted" data-testid={`report-resend-${r.id}`}>{t("reports.email.resend")}</button>
                )}
              </div>
            </summary>
            <div className="px-5 pb-5 grid md:grid-cols-3 gap-4 text-sm">
              <div><p className="eyebrow mb-2">{t("reports.new_claims")}</p>{r.new_claims.length === 0 ? <p className="text-muted-foreground">—</p> : r.new_claims.map((c) => <p key={c.id || `${c.severity}-${c.text.slice(0,40)}`} className="mb-1"><span className={`px-1.5 text-[10px] font-mono rounded ${c.severity === "red" ? "tag-red" : "tag-orange"}`}>{c.severity}</span> « {c.text} »</p>)}</div>
              <div><p className="eyebrow mb-2">{t("reports.scans")}</p>{r.scans.length === 0 ? <p className="text-muted-foreground">—</p> : r.scans.map((s) => <p key={s.id || s.url} className="mb-1 truncate">{s.url} · <b>{s.compliance_score}</b>/100</p>)}</div>
              <div><p className="eyebrow mb-2">{t("reports.expiring")}</p>{r.expiring_proofs.length === 0 ? <p className="text-muted-foreground">—</p> : r.expiring_proofs.map((p) => <p key={p.id || `${p.name}-${p.expires_at}`} className="mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> {p.name} · {p.expires_at}</p>)}</div>
            </div>
          </details>
        ))}
      </section>
    </div>
  );
}

function Trend({ cur, prev }) {
  const { t } = useLang();
  if (prev === null || prev === undefined) return <p className="text-sm text-muted-foreground">{t("reports.first")}</p>;
  const d = cur - prev;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  return <p className={`text-sm inline-flex items-center gap-1 ${d > 0 ? "text-[hsl(142_60%_32%)]" : d < 0 ? "text-red-600" : "text-muted-foreground"}`} data-testid="reports-trend-delta"><Icon className="w-4 h-4" /> {d > 0 ? "+" : ""}{d} {t("reports.vs")}</p>;
}
